/**
 * Group Sessions — discovery & registration.
 *
 * MVP flow:
 *   - Lists upcoming sessions role-filtered for the caller
 *   - Card per session with one-tap RSVP / Cancel RSVP
 *   - Eligible hosts (admin or verified-marketplace ADI) see a "Host a session" FAB
 *     which opens an inline create form
 *   - When registered, meeting_url is revealed + "Join session" button
 *
 * Clean styling, no gamification, no charts.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import { MobileDialog } from "../components/mobile";
import axios from "axios";
import { toast } from "sonner";
import { formatApiError } from "../utils/apiError";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  Video,
  Plus,
  X,
  Check,
  ExternalLink,
  BookOpen,
  ClipboardCheck,
  Download,
  CheckCircle2,
  Heart,
  Eye,
  GraduationCap,
  Clock3,
  Trash2,
  MessageSquare,
  Send,
  PoundSterling,
  Wallet,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  theory: { label: "Theory class", icon: BookOpen, tint: "bg-indigo-100 text-indigo-700" },
  mock_test: { label: "Mock-test workshop", icon: ClipboardCheck, tint: "bg-amber-100 text-amber-700" },
  nervous_learner_confidence: { label: "Confidence session", icon: Heart, tint: "bg-rose-100 text-rose-700" },
  hazard_perception: { label: "Hazard perception prep", icon: Eye, tint: "bg-sky-100 text-sky-700" },
  part_3_coaching: { label: "Part 3 coaching", icon: GraduationCap, tint: "bg-violet-100 text-violet-700" },
};

const TYPE_FILTERS = [
  { value: "all", label: "All" },
  { value: "theory", label: "Theory" },
  { value: "mock_test", label: "Mock" },
  { value: "nervous_learner_confidence", label: "Confidence" },
  { value: "hazard_perception", label: "Hazard" },
  { value: "part_3_coaching", label: "Part 3" },
];

const ROLE_OPTIONS = [
  { value: "any", label: "Anyone" },
  { value: "learner", label: "Learners" },
  { value: "pdi", label: "PDIs" },
  { value: "adi", label: "ADIs" },
];

const DEFAULT_FORM = {
  title: "",
  description: "",
  type: "theory",
  target_roles: ["any"],
  starts_at: "",
  duration_minutes: 60,
  capacity: 50,
  meeting_url: "",
  price_pounds: "",  // decimal pounds input; converted to pence on submit
};

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const prefix = sameDay
    ? "Today"
    : d.toDateString() === tomorrow.toDateString()
    ? "Tomorrow"
    : d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${prefix} · ${time}`;
}

function minutesUntil(iso) {
  if (!iso) return Infinity;
  return Math.round((new Date(iso) - Date.now()) / 60000);
}

function Badge({ children, tint = "bg-slate-100 text-slate-600" }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${tint}`}>
      {children}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HOST → ATTENDEES MESSAGE MODAL
   ═══════════════════════════════════════════════════════════════ */
function MessageAttendeesModal({ session, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("attendees");
  const [sending, setSending] = useState(false);
  const attendeeCount = session.attendee_count || 0;
  const waitlistCount = session.waitlist_count || 0;

  useEffect(() => {
    axios
      .get(`${API_URL}/api/group-sessions/message-templates`, { withCredentials: true })
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => setTemplates([]));
  }, []);

  const applyTemplate = (tpl) => {
    setSelectedKey(tpl.key);
    setBody(tpl.preview);
  };

  const recipientCount = useMemo(() => {
    if (audience === "attendees") return attendeeCount;
    if (audience === "waitlist") return waitlistCount;
    return attendeeCount + waitlistCount;
  }, [audience, attendeeCount, waitlistCount]);

  const send = async () => {
    if (!body.trim()) {
      toast.error("Write a message or pick a template");
      return;
    }
    setSending(true);
    try {
      const payload = { audience };
      // If the text is unchanged from the chosen template, send template_key
      // so the server can re-render with live session data (title/when/url).
      const current = templates.find((t) => t.key === selectedKey);
      if (current && current.preview.trim() === body.trim()) {
        payload.template_key = selectedKey;
      } else {
        payload.body = body.trim();
      }
      const { data } = await axios.post(
        `${API_URL}/api/group-sessions/${session.id}/message`,
        payload,
        { withCredentials: true },
      );
      toast.success(
        data?.delivered_to
          ? `Sent to ${data.delivered_to} ${data.delivered_to === 1 ? "person" : "people"}`
          : "No recipients to notify",
      );
      onSent && onSent(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <MobileDialog
      bareContent
      open
      onClose={onClose}
      testid="session-message-modal"
    >
      <div
        className="bg-white dark:bg-slate-800 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl"
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2 sticky top-0 bg-white dark:bg-slate-800">
          <MessageSquare className="w-4 h-4 text-indigo-600" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">
              Message attendees
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
              {session.title}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
            data-testid="session-message-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Audience selector */}
          <div>
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              Send to
            </p>
            <div className="flex gap-1.5 flex-wrap">
              {[
                { value: "attendees", label: `Attendees (${attendeeCount})` },
                { value: "waitlist", label: `Waitlist (${waitlistCount})` },
                { value: "all", label: `Everyone (${attendeeCount + waitlistCount})` },
              ].map((o) => {
                const active = audience === o.value;
                const disabled =
                  (o.value === "attendees" && attendeeCount === 0) ||
                  (o.value === "waitlist" && waitlistCount === 0) ||
                  (o.value === "all" && attendeeCount + waitlistCount === 0);
                return (
                  <button
                    key={o.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => setAudience(o.value)}
                    data-testid={`session-message-audience-${o.value}`}
                    className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      active
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Template chips */}
          {templates.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Quick templates
              </p>
              <div className="flex flex-wrap gap-1.5">
                {templates.map((t) => {
                  const active = selectedKey === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => applyTemplate(t)}
                      data-testid={`session-message-template-${t.key}`}
                      className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                        active
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Free-text editor */}
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5 block">
              Message
            </label>
            <textarea
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                // User typed — no longer an untouched template
                const current = templates.find((t) => t.key === selectedKey);
                if (current && current.preview !== e.target.value) {
                  // keep selectedKey highlighted but server will send as free text
                }
              }}
              rows={5}
              maxLength={2000}
              placeholder="Write a message — or pick a template above"
              data-testid="session-message-body"
              className="w-full text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              {body.length}/2000 characters · Sent to each person privately, tagged with the session title.
            </p>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center gap-2 sticky bottom-0 bg-white dark:bg-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !body.trim() || recipientCount === 0}
            data-testid="session-message-send"
            className="ml-auto inline-flex items-center gap-1.5 bg-indigo-600 text-white font-bold text-sm rounded-xl px-4 py-2.5 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed"
          >
            <Send className="w-3.5 h-3.5" />
            {sending ? "Sending…" : `Send to ${recipientCount}`}
          </button>
        </div>
      </div>
    </MobileDialog>
  );
}

function formatGbp(pence) {
  const v = (pence || 0) / 100;
  if (Number.isInteger(v)) return `£${v}`;
  return `£${v.toFixed(2)}`;
}

/* ═══════════════════════════════════════════════════════════════
   HOST EARNINGS WIDGET — compact ledger summary for hosts/admins
   ═══════════════════════════════════════════════════════════════ */
function HostEarningsWidget({ onOpenLedger }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API_URL}/api/group-sessions/payments/me/earnings/summary`, {
        withCredentials: true,
      })
      .then((r) => { if (alive) setSummary(r.data); })
      .catch(() => { if (alive) setSummary(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!summary) return null;
  const hasActivity =
    (summary.booking_count || 0) > 0 || (summary.refund_count || 0) > 0;
  if (!hasActivity) return null;

  return (
    <div
      data-testid="host-earnings-widget"
      className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-4 text-white shadow-md"
    >
      <div className="flex items-center gap-2 mb-3">
        <Wallet className="w-4 h-4" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-100">
          Your earnings
        </p>
        <button
          type="button"
          onClick={onOpenLedger}
          data-testid="host-earnings-view-ledger"
          className="ml-auto text-[11px] font-semibold text-emerald-100 hover:text-white underline underline-offset-2"
        >
          View ledger
        </button>
      </div>
      <div className="flex items-baseline gap-2">
        <p
          data-testid="host-earnings-balance"
          className="text-3xl font-black tracking-tight"
        >
          {formatGbp(summary.balance_pence)}
        </p>
        <p className="text-xs text-emerald-100">available balance</p>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div className="bg-white/10 rounded-lg py-2">
          <p className="text-[10px] text-emerald-100 uppercase tracking-wider">Gross</p>
          <p className="text-sm font-bold">{formatGbp(summary.gross_pence)}</p>
        </div>
        <div className="bg-white/10 rounded-lg py-2">
          <p className="text-[10px] text-emerald-100 uppercase tracking-wider">Bookings</p>
          <p className="text-sm font-bold">{summary.booking_count || 0}</p>
        </div>
        <div className="bg-white/10 rounded-lg py-2">
          <p className="text-[10px] text-emerald-100 uppercase tracking-wider">Fee (10%)</p>
          <p className="text-sm font-bold">{formatGbp(summary.platform_fees_pence)}</p>
        </div>
      </div>
      <p className="text-[10px] text-emerald-100 mt-2 leading-relaxed">
        Ledger-only for now — Stripe Connect payouts ship next. Admin can arrange a manual
        payout in the meantime.
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HOST EARNINGS LEDGER MODAL — full transaction history
   ═══════════════════════════════════════════════════════════════ */
function EarningsLedgerModal({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/group-sessions/payments/me/earnings/ledger`, {
        withCredentials: true,
      })
      .then((r) => setRows(r.data?.entries || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <MobileDialog
      bareContent
      open
      onClose={onClose}
      testid="earnings-ledger-modal"
    >
      <div
        className="bg-white dark:bg-slate-800 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl"
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2 sticky top-0 bg-white dark:bg-slate-800">
          <Wallet className="w-4 h-4 text-emerald-600" />
          <h3 className="text-sm font-bold text-slate-900 dark:text-white flex-1">
            Earnings ledger
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
            data-testid="earnings-ledger-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
              No transactions yet. Once attendees pay, entries land here in real time.
            </p>
          ) : (
            <ul className="divide-y divide-slate-200 dark:divide-slate-700">
              {rows.map((r) => {
                const isRefund = r.type === "refund_reversal";
                const sign = (r.amount_pence || 0) >= 0 ? "+" : "−";
                return (
                  <li
                    key={r.id}
                    data-testid={`earnings-ledger-row-${r.id}`}
                    className="py-3 flex items-start gap-3"
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isRefund
                          ? "bg-rose-100 text-rose-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {isRefund ? (
                        <Trash2 className="w-4 h-4" />
                      ) : (
                        <PoundSterling className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {r.description || (isRefund ? "Refund" : "Booking")}
                      </p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {new Date(r.created_at).toLocaleString(undefined, {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <p
                      className={`text-sm font-bold tabular-nums ${
                        isRefund ? "text-rose-600" : "text-emerald-700"
                      }`}
                    >
                      {sign}{formatGbp(Math.abs(r.amount_pence || 0))}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </MobileDialog>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ATTENDANCE LIST MODAL — host-only roster with paid/refund state
   ═══════════════════════════════════════════════════════════════ */
function AttendanceModal({ session, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/group-sessions/payments/${session.id}/attendance`, {
        withCredentials: true,
      })
      .then((r) => setData(r.data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Couldn't load attendance");
        setData({ attendees: [] });
      })
      .finally(() => setLoading(false));
  }, [session.id]);

  const attendees = data?.attendees || [];
  const paidCount = attendees.filter((a) => a.status === "paid").length;
  const refundedCount = attendees.filter((a) => a.status === "refunded").length;
  const registeredCount = attendees.filter((a) => a.status === "registered").length;

  return (
    <MobileDialog
      bareContent
      open
      onClose={onClose}
      testid="attendance-modal"
    >
      <div
        className="bg-white dark:bg-slate-800 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl"
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2 sticky top-0 bg-white dark:bg-slate-800">
          <Users className="w-4 h-4 text-indigo-600" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">
              Attendance
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
              {session.title}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
            data-testid="attendance-modal-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4">
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
          ) : attendees.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
              No attendees yet.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-3 text-[11px] font-semibold">
                {paidCount > 0 && (
                  <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 rounded-full px-2.5 py-1">
                    <Check className="w-3 h-3" />
                    {paidCount} paid
                  </span>
                )}
                {registeredCount > 0 && (
                  <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 rounded-full px-2.5 py-1">
                    <Check className="w-3 h-3" />
                    {registeredCount} registered
                  </span>
                )}
                {refundedCount > 0 && (
                  <span className="inline-flex items-center gap-1 bg-slate-200 text-slate-600 rounded-full px-2.5 py-1">
                    {refundedCount} refunded
                  </span>
                )}
              </div>
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {attendees.map((a) => (
                  <li
                    key={a.user_id + (a.booking_id || "")}
                    data-testid={`attendance-row-${a.user_id}`}
                    className="py-3 flex items-center gap-3"
                  >
                    <div className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 text-xs font-bold text-slate-600 dark:text-slate-300">
                      {(a.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {a.name}
                      </p>
                      {a.email && (
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {a.email}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {a.status === "paid" && (
                        <>
                          <p className="text-sm font-bold text-emerald-700 tabular-nums">
                            {formatGbp(a.amount_pence)}
                          </p>
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">
                            Paid
                          </p>
                        </>
                      )}
                      {a.status === "registered" && (
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
                          Registered
                        </p>
                      )}
                      {a.status === "refunded" && (
                        <>
                          <p className="text-sm font-semibold text-slate-500 line-through tabular-nums">
                            {formatGbp(a.amount_pence)}
                          </p>
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                            Refunded
                          </p>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </MobileDialog>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STRIPE CONNECT WIDGET — host onboarding state + CTA
   ═══════════════════════════════════════════════════════════════ */
function StripeConnectWidget() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const pull = useCallback(async (refresh = false) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/stripe-connect/status${refresh ? "?refresh=true" : ""}`,
        { withCredentials: true },
      );
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // On first mount: if the URL has ?connect=return, force a refresh from
    // Stripe so a just-onboarded host sees their enabled state immediately.
    const params = new URLSearchParams(window.location.search);
    const returning = params.get("connect") === "return";
    pull(returning);
    if (returning) {
      // Clean the URL after pulling
      const url = new URL(window.location.href);
      url.searchParams.delete("connect");
      window.history.replaceState({}, "", url.toString());
      toast.success("Onboarding complete — checking your account…");
    }
    if (params.get("connect") === "refresh") {
      const url = new URL(window.location.href);
      url.searchParams.delete("connect");
      window.history.replaceState({}, "", url.toString());
    }
  }, [pull]);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/stripe-connect/onboarding-link`,
        {},
        { withCredentials: true },
      );
      if (data?.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        toast.error("Couldn't start onboarding — try again");
        setStarting(false);
      }
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't start onboarding"));
      setStarting(false);
    }
  };

  const openDashboard = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/stripe-connect/dashboard-link`,
        { withCredentials: true },
      );
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open Stripe dashboard");
    }
  };

  if (loading) return null;
  // Figure out which state to render
  const onboarded = !!status?.onboarded;
  const hasAccount = !!status?.account_id;
  const currentlyDue = status?.currently_due || [];
  const pendingVerification = hasAccount && !onboarded;

  return (
    <div
      data-testid="stripe-connect-widget"
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4"
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            onboarded
              ? "bg-emerald-100 text-emerald-700"
              : pendingVerification
              ? "bg-amber-100 text-amber-700"
              : "bg-indigo-100 text-indigo-700"
          }`}
        >
          <Wallet className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              Payout account
            </p>
            {onboarded && (
              <span
                data-testid="connect-status-onboarded"
                className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5"
              >
                Connected
              </span>
            )}
            {pendingVerification && (
              <span
                data-testid="connect-status-pending"
                className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-2 py-0.5"
              >
                Action needed
              </span>
            )}
            {!hasAccount && (
              <span
                data-testid="connect-status-not-onboarded"
                className="text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600 rounded-full px-2 py-0.5"
              >
                Not connected
              </span>
            )}
          </div>
          {onboarded ? (
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
              Paid bookings go straight to your connected Stripe account — payouts land
              on Stripe's default schedule (usually every 2 business days).
            </p>
          ) : pendingVerification ? (
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
              Stripe still needs some details from you before payouts can start.
              {currentlyDue.length > 0 && (
                <>
                  {" "}
                  Outstanding: {currentlyDue.slice(0, 3).join(", ")}
                  {currentlyDue.length > 3 && "…"}
                </>
              )}
            </p>
          ) : (
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
              Connect a Stripe account so paid bookings can be deposited straight to your
              bank. Until you do, earnings are tracked in the ledger and paid out manually
              by admin.
            </p>
          )}

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {!onboarded && (
              <button
                type="button"
                onClick={startOnboarding}
                disabled={starting}
                data-testid="connect-start-onboarding"
                className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg px-3 py-2 hover:bg-indigo-700 disabled:bg-indigo-300"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {starting
                  ? "Opening…"
                  : pendingVerification
                  ? "Continue onboarding"
                  : "Connect with Stripe"}
              </button>
            )}
            {onboarded && (
              <button
                type="button"
                onClick={openDashboard}
                data-testid="connect-open-dashboard"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 border border-indigo-200 rounded-lg px-3 py-2"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open Stripe dashboard
              </button>
            )}
            {hasAccount && (
              <button
                type="button"
                onClick={() => pull(true)}
                data-testid="connect-refresh"
                className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 dark:hover:text-white px-2 py-2"
              >
                Refresh status
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SessionCard({ s, onRegister, onUnregister, onCancel, onEdit, onComplete, onDownloadIcs, onMessage, onPay, onOpenAttendance }) {
  const meta = TYPE_META[s.type] || TYPE_META.theory;
  const Icon = meta.icon;
  const mins = minutesUntil(s.starts_at);
  const live = s.status === "live" || (mins <= 0 && mins > -((s.duration_minutes || 60)));
  const startingSoon = !live && mins <= 30 && mins > 0;
  const closed = s.status === "closed";
  const cancelled = s.status === "cancelled";

  return (
    <div
      data-testid={`group-session-card-${s.id}`}
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl ${meta.tint} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge tint={meta.tint}>{meta.label}</Badge>
              {live && <Badge tint="bg-rose-100 text-rose-700">● Live</Badge>}
              {startingSoon && <Badge tint="bg-amber-100 text-amber-700">Starts in {mins}m</Badge>}
              {s.is_host && <Badge tint="bg-slate-900 text-white">You host</Badge>}
              {s.is_paid && (
                <Badge tint="bg-emerald-100 text-emerald-700">
                  <PoundSterling className="w-3 h-3" />
                  {formatGbp(s.price_pence)} / seat
                </Badge>
              )}
              {!s.is_paid && s.price_pence === 0 && (
                <Badge tint="bg-sky-100 text-sky-700">Free</Badge>
              )}
              {closed && <Badge tint="bg-emerald-100 text-emerald-700">Completed</Badge>}
              {cancelled && <Badge tint="bg-slate-200 text-slate-500">Cancelled</Badge>}
              {s.is_waitlisted && <Badge tint="bg-amber-100 text-amber-700">Waitlisted</Badge>}
            </div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white leading-snug truncate">
              {s.title}
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              Hosted by {s.host_name}
            </p>
          </div>
        </div>

        {s.description && (
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-3 line-clamp-2">
            {s.description}
          </p>
        )}

        <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500 dark:text-slate-400 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {formatWhen(s.starts_at)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {s.duration_minutes}m
          </span>
          <span className="inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {s.attendee_count}/{s.capacity}
          </span>
          {s.waitlist_count > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-600">
              <Clock3 className="w-3.5 h-3.5" />
              {s.waitlist_count} waiting
            </span>
          )}
        </div>
      </div>

      <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-700 flex items-center gap-2 flex-wrap">
        {/* ICS download is available for host / attendees / waitlisted */}
        {(s.is_host || s.is_registered || s.is_waitlisted) && !cancelled && !closed && (
          <button
            type="button"
            onClick={() => onDownloadIcs(s)}
            data-testid={`session-ics-${s.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white px-2 py-1.5"
            title="Add to calendar"
          >
            <Download className="w-3.5 h-3.5" />
            Add to calendar
          </button>
        )}

        {s.is_host ? (
          <>
            {!closed && !cancelled && (
              <>
                <button
                  type="button"
                  onClick={() => onOpenAttendance(s)}
                  data-testid={`session-attendance-${s.id}`}
                  className="text-xs font-semibold text-slate-700 dark:text-slate-200 hover:text-slate-900 px-3 py-1.5 inline-flex items-center gap-1"
                >
                  <Users className="w-3.5 h-3.5" />
                  Attendance
                </button>
                <button
                  type="button"
                  onClick={() => onMessage(s)}
                  data-testid={`session-message-${s.id}`}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 px-3 py-1.5 inline-flex items-center gap-1"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Message
                </button>
                <button
                  type="button"
                  onClick={() => onEdit(s)}
                  data-testid={`session-edit-${s.id}`}
                  className="text-xs font-semibold text-slate-700 dark:text-slate-200 hover:text-slate-900 px-3 py-1.5"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => onComplete(s)}
                  data-testid={`session-complete-${s.id}`}
                  className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 px-3 py-1.5 inline-flex items-center gap-1"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Mark complete
                </button>
                <button
                  type="button"
                  onClick={() => onCancel(s)}
                  data-testid={`session-cancel-${s.id}`}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-700 px-3 py-1.5 inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Cancel
                </button>
              </>
            )}
            {s.meeting_url && !cancelled && (
              <a
                href={s.meeting_url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`session-join-${s.id}`}
                className="ml-auto inline-flex items-center gap-1 text-xs font-bold bg-indigo-600 text-white rounded-lg px-3 py-2 hover:bg-indigo-700"
              >
                <Video className="w-3.5 h-3.5" />
                Open session
              </a>
            )}
          </>
        ) : s.is_registered ? (
          <>
            {!closed && !cancelled && (
              <button
                type="button"
                onClick={() => onUnregister(s)}
                data-testid={`session-unregister-${s.id}`}
                className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5"
              >
                Cancel RSVP
              </button>
            )}
            {s.meeting_url && !cancelled && (
              <a
                href={s.meeting_url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`session-join-${s.id}`}
                className="ml-auto inline-flex items-center gap-1 text-xs font-bold bg-indigo-600 text-white rounded-lg px-3 py-2 hover:bg-indigo-700"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Join session
              </a>
            )}
          </>
        ) : s.is_waitlisted ? (
          <button
            type="button"
            onClick={() => onUnregister(s)}
            data-testid={`session-leave-waitlist-${s.id}`}
            className="ml-auto text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Leave waitlist
          </button>
        ) : (
          <button
            type="button"
            onClick={() => (s.is_paid && !s.is_full ? onPay(s) : onRegister(s))}
            disabled={closed || cancelled}
            data-testid={
              s.is_paid && !s.is_full
                ? `session-pay-${s.id}`
                : `session-register-${s.id}`
            }
            className={`ml-auto inline-flex items-center gap-1 text-xs font-bold rounded-lg px-3 py-2 transition-colors ${
              s.is_full
                ? "bg-amber-500 text-white hover:bg-amber-600"
                : s.is_paid
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-indigo-600 text-white hover:bg-indigo-700"
            } disabled:bg-slate-300 disabled:cursor-not-allowed`}
          >
            {s.is_full ? (
              <>
                <Clock3 className="w-3.5 h-3.5" />
                Join waitlist
              </>
            ) : s.is_paid ? (
              <>
                <PoundSterling className="w-3.5 h-3.5" />
                Pay {formatGbp(s.price_pence)}
              </>
            ) : (
              <>
                <Check className="w-3.5 h-3.5" />
                RSVP
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function HostForm({ initial, onSave, onCancel, isEditing }) {
  const [form, setForm] = useState(initial || DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const toggleRole = (role) => {
    setForm((f) => {
      const has = f.target_roles.includes(role);
      let next;
      if (role === "any") {
        next = has ? [] : ["any"];
      } else {
        next = has
          ? f.target_roles.filter((r) => r !== role)
          : [...f.target_roles.filter((r) => r !== "any"), role];
      }
      return { ...f, target_roles: next.length ? next : ["any"] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        duration_minutes: Number(form.duration_minutes),
        capacity: Number(form.capacity),
        price_pence: Math.max(
          0,
          Math.round((parseFloat(form.price_pounds) || 0) * 100),
        ),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save — check the fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="group-session-form"
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900 dark:text-white">
          {isEditing ? "Edit session" : "Host a session"}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
          data-testid="session-form-cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <input
        type="text"
        placeholder="Title (e.g. Roundabouts Masterclass)"
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        required
        minLength={3}
        maxLength={120}
        data-testid="session-form-title"
        className="w-full text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      />

      <textarea
        placeholder="What will you cover? (optional)"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        maxLength={2000}
        rows={3}
        data-testid="session-form-desc"
        className="w-full text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
      />

      <div className="flex gap-2">
        <select
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
          data-testid="session-form-type"
          className="flex-1 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2"
        >
          <option value="theory">Theory class</option>
          <option value="mock_test">Mock-test workshop</option>
          <option value="nervous_learner_confidence">Confidence session (nervous learners)</option>
          <option value="hazard_perception">Hazard perception prep</option>
          <option value="part_3_coaching">Part 3 coaching</option>
        </select>
        <input
          type="datetime-local"
          value={form.starts_at}
          onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
          required
          data-testid="session-form-starts-at"
          className="flex-1 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2"
        />
      </div>

      <div className="flex gap-2">
        <label className="flex-1 text-xs text-slate-600 dark:text-slate-400">
          Duration (mins)
          <input
            type="number"
            min={15}
            max={240}
            value={form.duration_minutes}
            onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
            data-testid="session-form-duration"
            className="w-full mt-1 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2"
          />
        </label>
        <label className="flex-1 text-xs text-slate-600 dark:text-slate-400">
          Capacity
          <input
            type="number"
            min={1}
            max={200}
            value={form.capacity}
            onChange={(e) => setForm({ ...form, capacity: e.target.value })}
            data-testid="session-form-capacity"
            className="w-full mt-1 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2"
          />
        </label>
      </div>

      <input
        type="url"
        placeholder="Meeting URL (Zoom / Meet / Teams link)"
        value={form.meeting_url}
        onChange={(e) => setForm({ ...form, meeting_url: e.target.value })}
        required
        data-testid="session-form-url"
        className="w-full text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      />

      {/* Price (per seat) — empty / 0 = free session */}
      <label className="block text-xs text-slate-600 dark:text-slate-400">
        Price per seat
        <div className="relative mt-1">
          <PoundSterling className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="number"
            min="0"
            step="0.50"
            max="500"
            inputMode="decimal"
            placeholder="0.00 — leave blank for free"
            value={form.price_pounds}
            onChange={(e) => setForm({ ...form, price_pounds: e.target.value })}
            data-testid="session-form-price"
            className="w-full text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <p className="text-[10px] text-slate-400 mt-1">
          Platform takes 10% — you keep the rest. Refunded automatically if you cancel.
        </p>
      </label>

      <div>
        <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Who's it for?</p>
        <div className="flex flex-wrap gap-1.5">
          {ROLE_OPTIONS.map((r) => {
            const active = form.target_roles.includes(r.value);
            return (
              <button
                key={r.value}
                type="button"
                onClick={() => toggleRole(r.value)}
                data-testid={`session-form-role-${r.value}`}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                  active
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
                }`}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="submit"
        disabled={saving}
        data-testid="session-form-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 text-white font-bold text-sm rounded-xl px-4 py-3 hover:bg-indigo-700 disabled:bg-indigo-300"
      >
        {saving ? "Saving…" : isEditing ? "Save changes" : "Publish session"}
      </button>
    </form>
  );
}

export default function GroupSessions() {
  const navigate = useNavigate();
  const goBack = useSafeBack();
  const [filter, setFilter] = useState("all");
  const [sessions, setSessions] = useState([]);
  const [mySessions, setMySessions] = useState({ hosting: [], attending: [] });
  const [loading, setLoading] = useState(true);
  const [canHost, setCanHost] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pdiHostingEnabled, setPdiHostingEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, myRes, profileRes] = await Promise.all([
        axios.get(
          `${API_URL}/api/group-sessions${filter !== "all" ? `?type=${filter}` : ""}`,
          { withCredentials: true },
        ),
        axios.get(`${API_URL}/api/group-sessions/my`, { withCredentials: true }),
        axios.get(`${API_URL}/api/marketplace/my-profile`, { withCredentials: true }).catch(() => null),
      ]);
      setSessions(listRes.data?.sessions || []);
      setMySessions({
        hosting: myRes.data?.hosting || [],
        attending: myRes.data?.attending || [],
      });
      const meRes = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      const me = meRes.data || {};
      const adminUser = (me.role || "").toLowerCase() === "admin";
      const eligibleAdi = profileRes?.data?.eligibility?.eligible && profileRes?.data?.visible;
      setIsAdmin(adminUser);
      setCanHost(adminUser || !!eligibleAdi);
      if (adminUser) {
        try {
          const { data: s } = await axios.get(
            `${API_URL}/api/group-sessions/settings/platform`,
            { withCredentials: true },
          );
          setPdiHostingEnabled(!!s?.pdi_hosting_enabled);
        } catch { /* ignore */ }
      }
      // Surface waitlist promotions as toasts (unseen only, once)
      try {
        const { data: promoRes } = await axios.get(
          `${API_URL}/api/group-sessions/waitlist-promotions/me`,
          { withCredentials: true },
        );
        const unseen = (promoRes?.promotions || []).filter((p) => !p.seen);
        for (const p of unseen) {
          toast.success(`A seat opened — you're now in "${p.session_title}"`, {
            description: "Add it to your calendar from the session card.",
            duration: 7000,
          });
          axios.post(
            `${API_URL}/api/group-sessions/waitlist-promotions/${p.id}/seen`,
            {},
            { withCredentials: true },
          ).catch(() => {});
        }
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load sessions");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Post-Stripe-redirect handler — poll booking status until paid or show
  // a cancelled toast if the user bailed out. Strips the querystring so a
  // refresh doesn't re-trigger.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const bookingId = params.get("booking_id");
    const cancelled = params.get("booking_cancelled");
    if (!bookingId && !cancelled) return;

    const cleanUrl = () => {
      const url = new URL(window.location.href);
      url.search = "";
      window.history.replaceState({}, "", url.toString());
    };

    if (cancelled) {
      toast.info("Checkout cancelled. No charge was taken.", { duration: 5000 });
      cleanUrl();
      return;
    }

    // Poll up to ~30s for the webhook to flip the booking to paid.
    let cancelledFlag = false;
    const pending = toast.loading("Confirming your payment…");
    const maxAttempts = 15;
    const interval = 2000;

    const poll = async (attempt) => {
      if (cancelledFlag) return;
      try {
        const { data } = await axios.get(
          `${API_URL}/api/group-sessions/payments/bookings/${bookingId}/status`,
          { withCredentials: true },
        );
        if (data?.status === "paid") {
          toast.dismiss(pending);
          toast.success(`Payment confirmed — you're in "${data.session_title || "the session"}"`, {
            duration: 7000,
          });
          cleanUrl();
          fetchAll();
          return;
        }
        if (data?.status === "refunded") {
          toast.dismiss(pending);
          toast.info("Booking was refunded.");
          cleanUrl();
          return;
        }
      } catch {
        // 404 before webhook lands — keep polling
      }
      if (attempt + 1 < maxAttempts) {
        setTimeout(() => poll(attempt + 1), interval);
      } else {
        toast.dismiss(pending);
        toast.info(
          "We're still finalising your payment — it'll appear in a moment. Refresh in a minute.",
          { duration: 8000 },
        );
        cleanUrl();
        fetchAll();
      }
    };
    poll(0);
    return () => {
      cancelledFlag = true;
      toast.dismiss(pending);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePdiHosting = async () => {
    const next = !pdiHostingEnabled;
    try {
      await axios.put(
        `${API_URL}/api/group-sessions/settings/platform`,
        { pdi_hosting_enabled: next },
        { withCredentials: true },
      );
      setPdiHostingEnabled(next);
      toast.success(next ? "PDIs can now host sessions" : "PDI hosting disabled");
    } catch {
      toast.error("Couldn't save setting");
    }
  };

  const handleRegister = async (s) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/group-sessions/${s.id}/register`,
        {},
        { withCredentials: true },
      );
      if (data?.waitlisted) {
        toast.success("Added to the waitlist — we'll promote you if a seat opens.");
      } else {
        toast.success("You're in. See you there.");
      }
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't RSVP");
    }
  };

  const handleUnregister = async (s) => {
    try {
      await axios.post(`${API_URL}/api/group-sessions/${s.id}/unregister`, {}, { withCredentials: true });
      toast.success(s.is_waitlisted ? "Removed from waitlist" : "RSVP cancelled");
      fetchAll();
    } catch {
      toast.error("Couldn't update — try again");
    }
  };

  const handlePay = async (s) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/group-sessions/${s.id}/checkout`,
        {},
        { withCredentials: true },
      );
      if (data?.checkout_url) {
        // Full-page redirect so Stripe's hosted page takes over.
        window.location.href = data.checkout_url;
      } else {
        toast.error("Checkout couldn't start — try again");
      }
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't start checkout"));
    }
  };

  const handleComplete = async (s) => {
    if (!window.confirm(`Mark "${s.title}" as complete? The session will close for new sign-ups.`)) {
      return;
    }
    try {
      const { data } = await axios.post(
        `${API_URL}/api/group-sessions/${s.id}/complete`,
        {},
        { withCredentials: true },
      );
      toast.success(`Session closed · ${data.final_attendee_count} attended`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't mark complete");
    }
  };

  const handleDownloadIcs = async (s) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/group-sessions/${s.id}/ics`,
        { withCredentials: true, responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "text/calendar;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cooks-session-${s.id}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Added to your calendar");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't download calendar file");
    }
  };

  const handleCancel = async (s) => {
    if (!window.confirm(`Cancel "${s.title}"? Attendees will be notified next time they open the app.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/group-sessions/${s.id}`, { withCredentials: true });
      toast.success("Session cancelled");
      fetchAll();
    } catch {
      toast.error("Couldn't cancel session");
    }
  };

  const handleSave = async (payload) => {
    if (editing) {
      await axios.put(`${API_URL}/api/group-sessions/${editing.id}`, payload, { withCredentials: true });
      toast.success("Session updated");
    } else {
      await axios.post(`${API_URL}/api/group-sessions`, payload, { withCredentials: true });
      toast.success("Session published");
    }
    setShowForm(false);
    setEditing(null);
    fetchAll();
  };

  const openEdit = (s) => {
    // convert ISO to datetime-local value
    const iso = s.starts_at;
    const localDT = iso
      ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16)
      : "";
    setEditing(s);
    setShowForm(true);
    setTimeout(() => {
      // Prefill fields through a tiny hack: we pass `initial` via the key change
    }, 0);
    setInitialForEdit({
      title: s.title,
      description: s.description || "",
      type: s.type,
      target_roles: s.target_roles || ["any"],
      starts_at: localDT,
      duration_minutes: s.duration_minutes,
      capacity: s.capacity,
      meeting_url: s.meeting_url || "",
      price_pounds:
        s.price_pence > 0 ? (s.price_pence / 100).toFixed(2).replace(/\.00$/, "") : "",
    });
  };

  const [initialForEdit, setInitialForEdit] = useState(DEFAULT_FORM);
  const [messagingSession, setMessagingSession] = useState(null);
  const [attendanceSession, setAttendanceSession] = useState(null);
  const [showLedger, setShowLedger] = useState(false);

  const upcoming = useMemo(
    () => sessions.filter((s) => s.status !== "cancelled"),
    [sessions],
  );

  return (
    <div data-testid="group-sessions-page" className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700"
            data-testid="group-sessions-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">Group Sessions</h1>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Live theory classes & mock-test workshops
            </p>
          </div>
          {canHost && !showForm && (
            <button
              type="button"
              onClick={() => { setEditing(null); setInitialForEdit(DEFAULT_FORM); setShowForm(true); }}
              data-testid="host-session-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-indigo-600 text-white rounded-lg px-3 py-2 hover:bg-indigo-700"
            >
              <Plus className="w-3.5 h-3.5" />
              Host
            </button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="max-w-3xl mx-auto px-4 pb-3 flex gap-1.5">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              data-testid={`session-filter-${f.value}`}
              className={`text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors ${
                filter === f.value
                  ? "bg-indigo-600 text-white"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {isAdmin && (
          <div
            data-testid="admin-pdi-toggle"
            className="bg-slate-900 text-white rounded-xl p-3 flex items-center gap-3"
          >
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Admin</p>
              <p className="text-sm font-semibold">Let PDIs host sessions</p>
              <p className="text-[11px] text-slate-400">
                Off by default. Turn on to open Part 3 coaching rooms to subscribed PDIs.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={pdiHostingEnabled}
              onClick={togglePdiHosting}
              data-testid="pdi-hosting-toggle"
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                pdiHostingEnabled ? "bg-emerald-500" : "bg-slate-600"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  pdiHostingEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        )}

        {showForm && (
          <HostForm
            key={editing ? editing.id : "new"}
            initial={initialForEdit}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditing(null); }}
            isEditing={!!editing}
          />
        )}

        {/* Host earnings widget (rendered only when the user has host activity) */}
        {canHost && (
          <HostEarningsWidget onOpenLedger={() => setShowLedger(true)} />
        )}

        {/* Stripe Connect onboarding state — always rendered for eligible hosts
            (admin + ADIs + PDIs when toggle on) so they can start getting paid
            out directly, even before any earnings exist. */}
        {canHost && <StripeConnectWidget />}

        {/* My sessions */}
        {(mySessions.hosting.length > 0 || mySessions.attending.length > 0) && (
          <div className="space-y-2">
            <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Your sessions
            </h2>
            {[...mySessions.hosting, ...mySessions.attending].map((s) => (
              <SessionCard
                key={`my-${s.id}`}
                s={s}
                onRegister={handleRegister}
                onUnregister={handleUnregister}
                onCancel={handleCancel}
                onEdit={openEdit}
                onComplete={handleComplete}
                onDownloadIcs={handleDownloadIcs}
                onMessage={setMessagingSession}
                onPay={handlePay}
                onOpenAttendance={setAttendanceSession}
              />
            ))}
          </div>
        )}

        {/* Upcoming */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Upcoming
            </h2>
            <span className="text-[10px] text-slate-400">{upcoming.length} sessions</span>
          </div>
          {loading ? (
            <div className="text-center py-12 text-sm text-slate-400">Loading…</div>
          ) : upcoming.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
              <Video className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No upcoming sessions yet.
              </p>
              {canHost && (
                <p className="text-xs text-slate-400 mt-1">
                  Be the first to host one.
                </p>
              )}
            </div>
          ) : (
            upcoming.map((s) => (
              <SessionCard
                key={s.id}
                s={s}
                onRegister={handleRegister}
                onUnregister={handleUnregister}
                onCancel={handleCancel}
                onEdit={openEdit}
                onComplete={handleComplete}
                onDownloadIcs={handleDownloadIcs}
                onMessage={setMessagingSession}
                onPay={handlePay}
                onOpenAttendance={setAttendanceSession}
              />
            ))
          )}
        </div>
      </div>

      {messagingSession && (
        <MessageAttendeesModal
          session={messagingSession}
          onClose={() => setMessagingSession(null)}
          onSent={() => fetchAll()}
        />
      )}
      {attendanceSession && (
        <AttendanceModal
          session={attendanceSession}
          onClose={() => setAttendanceSession(null)}
        />
      )}
      {showLedger && (
        <EarningsLedgerModal onClose={() => setShowLedger(false)} />
      )}
    </div>
  );
}
